import { inertiaFactor, SHIFT_SECONDS } from './carDynamics';
import { calibratedCar } from './carCalibration';
import { AIR_DENSITY, conditionPhysics, GRAVITY, raceConditions, TRACTION_CONTROL_TARGET } from './raceConditions';

/**
 * The race (SPEC-008 PR-8.3).
 *
 * A longitudinal-dynamics integration of two cars from rest over 400 metres,
 * returning both traces in PHYSICAL UNITS and a verdict. Metres, seconds and
 * km/h — never viewBox coordinates and never a formatted string, for the reason
 * `accelerationTrace.js` gives for the same choice: the mapping onto a strip
 * needs to know how wide the strip is, and this module must not.
 *
 * Four forces and one constraint, which is the whole of it. The engine makes a
 * force through a gear; the tyre can only transmit what the road will hold; drag,
 * rolling resistance and gravity take their share; what is left divides by the
 * mass and the rotating parts. Everything interesting the screen shows —
 * why all-wheel drive is a different car on snow, why turning traction control
 * off costs a Ferrari two seconds in the wet and costs a 959 nothing — falls out
 * of the constraint rather than being written down anywhere.
 *
 * Pure. No React, no browser, no clock — the caller plays this back, and the
 * playback is `application/motion`'s problem.
 */

/** The metric standing start, in the units the whole catalogue is stored in. */
export const RACE_DISTANCE_METRES = 400;

/**
 * The integration step, and how often a step is kept.
 *
 * 200 Hz because the weight-transfer term reads the PREVIOUS step's
 * acceleration — an explicit solve for something that is genuinely implicit —
 * and at 20 Hz that lag is visible as a launch that grips a frame too late.
 * Samples come out at 20 Hz because that is a playback rate, and keeping ten
 * times more of them would be ten times the array for a position nobody can see.
 */
export const STEP_SECONDS = 0.005;
const SAMPLE_SECONDS = 0.05;

/**
 * How long a race is allowed to take before it is called.
 *
 * Generous on purpose. On snow, up the climb, a rear-drive car needs the better
 * part of a minute, and a car stopped six metres short by a stopwatch reads as a
 * bug rather than as a fact about snow. The bound is here for the genuinely
 * stuck case — a record whose power cannot overcome its own gradient — which is
 * a designed state rather than an impossible one, because the catalogue is
 * fetched and can arrive carrying anything.
 */
const RACE_LIMIT_SECONDS = 75;

/** Under these, the two arrived together and neither of them beat the other. */
const DEAD_HEAT_SECONDS = 0.01;
const DEAD_HEAT_METRES = 1;

/** A car still this slow after a second of full throttle is not going to move. */
const STALLED_AFTER_SECONDS = 1;
const STALLED_BELOW_MS = 0.1;

/**
 * One car, integrated.
 *
 * `stopAtKmh` is what the calibration uses: the same integration, run to a speed
 * instead of to a distance, so the figure the model is fitted against is
 * measured by exactly the code that will later race the car. Measuring it any
 * other way would calibrate one model and run another.
 */
function integrate(dynamics, physics, { distance = RACE_DISTANCE_METRES, stopAtKmh = null } = {}) {
    const samples = [];
    const topGear = dynamics.ratios.length - 1;
    let speed = 0;
    let travelled = 0;
    let elapsed = 0;
    let gear = 0;
    let shiftingUntil = -1;
    let acceleration = 0;
    let wheelspinSeconds = 0;
    let zeroToHundred = null;
    let topSpeed = 0;

    while (elapsed < RACE_LIMIT_SECONDS) {
        const shifting = elapsed < shiftingUntil;
        // In first gear the clutch holds the engine at the launch speed until the
        // road catches up with it; above that the engine is tied to the wheels.
        const omega = Math.max(speed * dynamics.ratios[gear], gear === 0 ? dynamics.launchOmega : 0);
        const demand = shifting
            ? 0
            : dynamics.torqueAt(Math.min(omega, dynamics.redline)) * dynamics.ratios[gear] * dynamics.efficiency;

        // The load on the driven axle: what is parked on it, plus what the car's
        // own acceleration has just thrown backwards onto it.
        const parked = dynamics.mass * GRAVITY * physics.gradeCosine * dynamics.drivenShare;
        const transferred = dynamics.transferSign * dynamics.mass * acceleration * dynamics.transferRatio;
        const load = Math.max(0, parked + transferred);
        const held = physics.peak * dynamics.grip * load;
        const sliding = physics.slide * dynamics.grip * load;

        let drive;
        let spinning;

        if (physics.isAided) {
            // The aid holds the tyre just under its own limit, so it never
            // spins — and it never uses the last twentieth either, which is what
            // it costs a car that would not have spun anyway.
            drive = Math.min(demand, held * TRACTION_CONTROL_TARGET);
            spinning = false;
        } else if (demand > held) {
            // Past the peak the whole contact patch lets go at once and what is
            // left is the sliding coefficient. This is the cliff the aid exists
            // to keep the car off, and it is why switching it off costs a
            // rear-drive car whole seconds in the wet.
            drive = sliding;
            spinning = true;
        } else {
            // Below the limit the unaided car gets everything the engine makes,
            // including the margin the aid keeps back.
            drive = demand;
            spinning = false;
        }

        const drag = 0.5 * AIR_DENSITY * dynamics.dragArea * speed * speed;
        const rolling = physics.rolling * dynamics.mass * GRAVITY * physics.gradeCosine;
        const gravity = dynamics.mass * GRAVITY * physics.gradeSine;

        acceleration = (drive - drag - rolling - gravity) / (dynamics.mass * inertiaFactor(dynamics, gear));

        if (speed <= 0 && acceleration <= 0) {
            acceleration = 0;
        }

        speed = Math.max(0, speed + acceleration * STEP_SECONDS);
        travelled += speed * STEP_SECONDS;
        elapsed += STEP_SECONDS;
        topSpeed = Math.max(topSpeed, speed);

        if (spinning) {
            wheelspinSeconds += STEP_SECONDS;
        }

        if (zeroToHundred === null && speed * 3.6 >= 100) {
            zeroToHundred = elapsed;
        }

        if (samples.length * SAMPLE_SECONDS <= elapsed) {
            samples.push({
                seconds: elapsed,
                metres: travelled,
                kmh: speed * 3.6,
                gear: gear + 1,
                wheelspin: spinning,
            });
        }

        if (stopAtKmh !== null && speed * 3.6 >= stopAtKmh) {
            break;
        }

        if (travelled >= distance) {
            break;
        }

        // The car will not move at all — a gradient its grip cannot answer.
        if (elapsed > STALLED_AFTER_SECONDS && speed < STALLED_BELOW_MS) {
            break;
        }

        if (!shifting && gear < topGear && speed * dynamics.ratios[gear] >= dynamics.redline) {
            gear += 1;
            shiftingUntil = elapsed + SHIFT_SECONDS;
        }
    }

    return {
        samples,
        seconds: elapsed,
        metres: travelled,
        finished: travelled >= distance,
        trapKmh: speed * 3.6,
        topKmh: topSpeed * 3.6,
        zeroToHundred,
        wheelspinSeconds,
    };
}

/**
 * The 0–100 the calibration is fitted against.
 *
 * Dry, flat and aided, because those are the conditions every figure in the
 * catalogue was measured under. Exported for the same reason it is a named
 * function rather than an inline closure: it is the definition of what the
 * model is being made to agree with, and a test asserts against it directly.
 */
export function measureZeroToHundred(dynamics) {
    const physics = conditionPhysics(raceConditions({}));
    const run = integrate(dynamics, physics, { distance: Infinity, stopAtKmh: 100 });

    return run.zeroToHundred ?? RACE_LIMIT_SECONDS;
}

/**
 * A calibrated car, ready to race. Memoised by the caller, not here — this
 * module has no idea how long a record lives.
 */
export function raceableCar(car) {
    return calibratedCar(car, measureZeroToHundred);
}

/**
 * Where this car was at a given moment.
 *
 * The DISTANCE is interpolated between the two samples either side, because it
 * is what positions a car on a strip and a car that jumped twenty times a second
 * would read as a stutter rather than as a car. Everything else — the speed, the
 * gear, whether it is spinning — is taken from the sample itself: interpolating
 * a gear number would produce third-and-a-half, and interpolating a boolean is
 * not a thing.
 *
 * It lives here rather than in the hook that plays it back so that the sampling
 * interval stays this module's business. A caller that had to divide by it would
 * be a caller that knew how finely this file chose to record.
 */
export function laneAt(lane, seconds) {
    const { samples } = lane;

    if (!samples.length) {
        return { metres: 0, kmh: 0, gear: 1, wheelspin: false };
    }

    const index = Math.max(0, Math.min(Math.floor(seconds / SAMPLE_SECONDS), samples.length - 1));
    const sample = samples[index];
    const next = samples[index + 1];
    const span = next ? next.seconds - sample.seconds : 0;
    const share = span > 0 ? Math.min(1, Math.max(0, (seconds - sample.seconds) / span)) : 0;

    return {
        metres: seconds <= 0 ? 0 : sample.metres + ((next?.metres ?? sample.metres) - sample.metres) * share,
        kmh: sample.kmh,
        gear: sample.gear,
        wheelspin: sample.wheelspin,
    };
}

/**
 * Which of the two won, and by how much.
 *
 * On TIME where both reached the line, and on DISTANCE where they did not,
 * because a race neither car finished still has a car that got further and
 * saying "no result" would throw away the only fact the run produced (D-12).
 *
 * The margin is reported twice on purpose (D-13). Seconds is what a stopwatch
 * gives and is the number a reader would quote. Metres is where the loser
 * actually was when the winner crossed, which is the gap the strip has just
 * drawn — and on snow the two are wildly different numbers about the same race.
 */
function judge(lanes) {
    const [first, second] = lanes;
    const gap = (winner, loser) => ({
        winner: winner.carId,
        // A time gap only exists where both cars stopped the same clock. Where
        // one of them never reached the line there is no second time to
        // subtract, and reporting the race limit as a margin would be reporting
        // the stopwatch rather than the cars.
        marginSeconds: winner.finished && loser.finished ? Math.abs(winner.seconds - loser.seconds) : 0,
        marginMetres: winner.finished
            ? Math.max(0, RACE_DISTANCE_METRES - laneAt(loser, winner.seconds).metres)
            : Math.abs(winner.metres - loser.metres),
    });

    if (first.finished && second.finished) {
        return Math.abs(first.seconds - second.seconds) < DEAD_HEAT_SECONDS
            ? { winner: null, marginSeconds: 0, marginMetres: 0 }
            : gap(...(first.seconds < second.seconds ? [first, second] : [second, first]));
    }

    if (first.finished !== second.finished) {
        return gap(...(first.finished ? [first, second] : [second, first]));
    }

    // Neither reached the line, so the race is decided on how far they got —
    // which on a snow climb is often "one of them did not move at all", and is
    // still a result rather than an absence of one.
    return Math.abs(first.metres - second.metres) < DEAD_HEAT_METRES
        ? { winner: null, marginSeconds: 0, marginMetres: 0 }
        : gap(...(first.metres > second.metres ? [first, second] : [second, first]));
}

/**
 * Two cars, one strip.
 *
 * Returns null rather than an empty race for a pair the catalogue cannot supply
 * the figures for — the same argument `accelerationTrace` makes for answering
 * null: a strip with two motionless cars on it reads as a broken screen, and the
 * absence of the band is the honest answer.
 *
 * `dynamics` is taken as an argument rather than derived here so the caller can
 * memoise the expensive half. Calibrating a car is forty integrations; running a
 * race is one, and the reader changes the surface far more often than they
 * change the car.
 */
export function dragRace(entries, conditions) {
    if (!Array.isArray(entries) || entries.length !== 2 || entries.some((entry) => !entry?.dynamics)) {
        return null;
    }

    const parsed = raceConditions(conditions);
    const physics = conditionPhysics(parsed);
    const lanes = entries.map(({ carId, dynamics }) => ({
        carId,
        // The dry, flat, aided figure this car's record publishes, carried
        // through so the reveal can say what these conditions cost it without
        // reaching back into the catalogue for a second copy of it.
        publishedZeroToHundred: dynamics.publishedZeroToHundred,
        ...integrate(dynamics, physics),
    }));

    return {
        distance: RACE_DISTANCE_METRES,
        conditions: parsed,
        lanes,
        // The length of the whole event, which is what the playback is scaled
        // against — the slower car, because a strip that stopped when the winner
        // crossed would never show the loser arrive.
        duration: Math.max(...lanes.map((lane) => lane.seconds)),
        ...judge(lanes),
    };
}

export { RACE_LIMIT_SECONDS };
